/**
 * Command Palette — Ctrl+K / Cmd+K opens a fuzzy-search modal over
 * the terminal's catalog of commands. Selected command is pre-filled
 * into the terminal input (or executed directly on Shift+Enter).
 *
 * Keyboard:
 *   ↑ ↓        navigate results
 *   Enter      pre-fill the command into terminal input
 *   Shift+↵    pre-fill AND execute immediately
 *   Esc        close
 *   Ctrl+K     open / close from anywhere
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

interface CatalogItem {
  command: string
  description: string
  category: string
}

interface CatalogResponse {
  commands: CatalogItem[]
  categories: string[]
  total: number
}

interface Props {
  open: boolean
  onClose: () => void
  onSelect: (command: string, execute: boolean) => void
}

// Simple fuzzy score: matches each query character in order, rewards
// consecutive matches + matches at word boundaries. Good enough for
// ~70 commands; no library needed.
function fuzzyScore(query: string, text: string): number {
  if (!query) return 0
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (t.includes(q)) return 1000 - t.indexOf(q) // contiguous match wins

  let score = 0
  let qi = 0
  let consecutive = 0
  let wordStart = true
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    const tc = t[ti]
    if (tc === q[qi]) {
      score += 10
      if (wordStart) score += 20
      consecutive++
      score += consecutive * 5
      qi++
    } else {
      consecutive = 0
    }
    wordStart = tc === ' ' || tc === '/' || tc === '-'
  }
  if (qi < q.length) return 0 // not all chars matched
  return score
}

export function CommandPalette({ open, onClose, onSelect }: Props) {
  const [catalog, setCatalog] = useState<CatalogItem[] | null>(null)
  const [query, setQuery] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Lazy-load catalog on first open
  useEffect(() => {
    if (open && catalog === null) {
      api.get<CatalogResponse>('/api/terminal/commands')
        .then(r => setCatalog(r.data.commands))
        .catch(() => setCatalog([]))
    }
  }, [open, catalog])

  // Focus input when opened, reset state
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIdx(0)
      const t = setTimeout(() => inputRef.current?.focus(), 10)
      return () => clearTimeout(t)
    }
  }, [open])

  // Filter & rank by query
  const filtered = useMemo(() => {
    if (!catalog) return []
    if (!query.trim()) return catalog
    return catalog
      .map(item => ({
        item,
        score: Math.max(
          fuzzyScore(query, item.command) * 1.5,
          fuzzyScore(query, item.description),
          fuzzyScore(query, item.category) * 0.5,
        ),
      }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(x => x.item)
  }, [catalog, query])

  // Clamp selection
  useEffect(() => {
    if (selectedIdx >= filtered.length) setSelectedIdx(0)
  }, [filtered.length, selectedIdx])

  // Scroll selected into view
  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${selectedIdx}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIdx])

  const select = useCallback((execute: boolean) => {
    const item = filtered[selectedIdx]
    if (!item) return
    // Strip TICKER / [N] / etc. placeholders so the input line is clean
    const cmd = item.command.replace(/\s+(TICKER|KEYWORD|SEARCH|\[[^\]]+\]).*$/i, '')
    onSelect(cmd, execute)
    onClose()
  }, [filtered, selectedIdx, onSelect, onClose])

  const handleKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(e.shiftKey)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }, [filtered.length, select, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="w-full max-w-xl mx-4 bg-surface-card border border-crt-green/40 rounded-lg shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-crt-green/20">
          <Search size={14} className="text-crt-dim shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Type a command or description…"
            aria-label="Search commands"
            className="flex-1 bg-transparent text-crt-green font-mono text-sm placeholder:text-crt-dim/50 outline-none"
          />
          <kbd className="text-[10px] text-crt-dim font-mono px-1.5 py-0.5 border border-crt-dim/30 rounded">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-[60vh] overflow-y-auto crt-scrollbar"
        >
          {catalog === null ? (
            <div className="px-4 py-6 text-crt-dim text-xs">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-6 text-crt-dim text-xs">
              No matches. Try /help for categories or /search {query}.
            </div>
          ) : (
            filtered.map((item, i) => (
              <button
                key={item.command + ':' + item.category}
                type="button"
                data-idx={i}
                onClick={() => {
                  setSelectedIdx(i)
                  select(false)
                }}
                onDoubleClick={() => {
                  setSelectedIdx(i)
                  select(true)
                }}
                className={clsx(
                  'w-full text-left px-4 py-2 flex items-center gap-3 font-mono text-xs border-l-2 transition-colors',
                  i === selectedIdx
                    ? 'bg-crt-green/10 border-crt-green text-crt-bright'
                    : 'border-transparent text-crt-green hover:bg-crt-green/5',
                )}
              >
                <span className="font-bold shrink-0 w-44 truncate">{item.command}</span>
                <span className="text-crt-dim flex-1 truncate">{item.description}</span>
                <span className="text-[10px] text-crt-amber/70 shrink-0">{item.category}</span>
              </button>
            ))
          )}
        </div>

        {/* Hint footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-crt-green/20 bg-black/30 text-[10px] text-crt-dim font-mono">
          <span>
            <kbd className="mx-0.5">↑↓</kbd> navigate · <kbd className="mx-0.5">↵</kbd> fill ·{' '}
            <kbd className="mx-0.5">⇧↵</kbd> execute · <kbd className="mx-0.5">Esc</kbd> close
          </span>
          <span>{filtered.length}/{catalog?.length ?? 0}</span>
        </div>
      </div>
    </div>
  )
}
